import { onValue, Reference } from "firebase/database";
import { useEffect, useState } from "react";
import { useMemoCompare } from "./useMemoCompare";

type TStatusPromise = "idle" | "loading" | "success" | "error";

/**
 * hook que trata dados do firebase a partir de uma ref,
 * retornado valor e status atual
 * @param ref Reference do firebase onde se deseja obter dados
 * @returns : { value - valor de resposta do firebase,
 * error: erro de resposta do firebase,
 * status: status da requisição do firebase ("idle" | "loading" | "success" | "error"),
 * loading: true se o status for "loading"
 *  }
 */
export function useFiresbaseQuery<T>(ref: Reference | undefined) {
    const [value, setValue] = useState<Array<T>>();
    const [status, setStatus] = useState<TStatusPromise>("idle");
    const [error, setError] = useState<Error>();

    // Armazenado valor da ref, garantindo que não irá mudar para
    // uma mesma ref com problema de instancias diferentes do objeto
    const refCached = useMemoCompare(ref, (prevRef) => {
        // Usa como comparaçãoo de refs a função própria do Reference do firebase
        return prevRef && ref && ref.isEqual(prevRef);
    });
    useEffect(() => {
        // Se a ref não tiver valor, não prossegue com listener de firebase
        if (!refCached) {
            setValue([]);
            setStatus("idle");
            return;
        }
        setStatus("loading");
        // adiciona o listener a query
        const unsubscribe = onValue(
            refCached,
            (snap) => {
                // Get data for collection or doc
                const data: Array<T> = [];
                snap.forEach((s) => {
                    data.push(s.val());
                });

                setStatus("success");
                setValue(data);
            },
            (error) => {
                setError(error);
                setStatus("error");
            }
        );

        // retorna a função de unsubcribe para sempre que o
        // remover o listener sempre que refCached mudar ou o componente
        // desomntar
        return unsubscribe;
    }, [refCached]); // roda sempre que refCached mudar

    return { value, status, error, loading: status === "loading" };
}
